import { cloneElement, useCallback, useEffect, useRef } from "react"

/**
 * A component that enhances its child element by triggering a callback when the mouse enters
 * an extended area around the element.
 */
export function ParadiseEnhancedBox({
  children,
  onMouseEnter,
  threshold = 100,
  placement = "top",
}: {
  children: React.ReactElement
  onMouseEnter: () => void
  /**
   * Distance in pixels to extend the element's bounding box
   */
  threshold?: number
  /**
   * The placement of the extended area relative to the element
   */
  placement?: "top" | "bottom" | "left" | "right"
}) {
  const targetRef = useRef<HTMLElement>(null)
  const hasEntered = useRef(false)

  const getExtendedRect = useCallback(
    (rect: DOMRect) => {
      switch (placement) {
        case "top":
          return {
            top: rect.top - threshold,
            bottom: rect.bottom,
            left: rect.left - threshold,
            right: rect.right + threshold,
          }

        case "bottom":
          return {
            top: rect.top,
            bottom: rect.bottom + threshold,
            left: rect.left - threshold,
            right: rect.right + threshold,
          }

        case "left":
          return {
            top: rect.top - threshold,
            bottom: rect.bottom + threshold,
            left: rect.left,
            right: rect.right + threshold,
          }

        case "right":
          return {
            top: rect.top - threshold,
            bottom: rect.bottom + threshold,
            left: rect.left - threshold,
            right: rect.right,
          }
      }
    },
    [placement, threshold]
  )

  const handleWindowMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!targetRef.current) {
        return
      }

      const rect = targetRef.current.getBoundingClientRect()

      const extendedRect = getExtendedRect(rect)

      const mouseX = e.clientX
      const mouseY = e.clientY

      const isInExpandedArea =
        mouseX >= extendedRect.left &&
        mouseX <= extendedRect.right &&
        mouseY >= extendedRect.top &&
        mouseY <= extendedRect.bottom

      if (isInExpandedArea && !hasEntered.current) {
        hasEntered.current = true
        onMouseEnter()
      } else if (!isInExpandedArea) {
        hasEntered.current = false
      }
    },
    [onMouseEnter, getExtendedRect]
  )

  useEffect(() => {
    if (!targetRef.current) {
      return
    }

    window.addEventListener("mousemove", handleWindowMouseMove)
    return () => window.removeEventListener("mousemove", handleWindowMouseMove)
  }, [handleWindowMouseMove, getExtendedRect])

  const childWithRef = cloneElement(children, {
    ref: targetRef,
  })

  return childWithRef
}
